import { ApolloClient, InMemoryCache, createHttpLink, split, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { authEvents } from '../services/authEvents';

// Configure your GraphQL endpoint here
// This should point to your Oathkeeper proxy which validates cookies and forwards to Hasura
const GRAPHQL_HTTP_ENDPOINT = process.env.REACT_APP_HASURA_ENDPOINT || 'https://graphql.familypal.com/v1/graphql';
const GRAPHQL_WS_ENDPOINT = GRAPHQL_HTTP_ENDPOINT.replace(/^https?/, 'wss');

// HTTP link for queries and mutations
// credentials: 'include' sends cookies to the proxy for authentication
const httpLink = createHttpLink({
  uri: GRAPHQL_HTTP_ENDPOINT,
  credentials: 'include', // Send cookies for Oathkeeper/proxy auth
});

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: GRAPHQL_WS_ENDPOINT,
    // For WebSocket, cookies are sent automatically if same origin
    // Reconnect on connection loss
    retryAttempts: 5,
    shouldRetry: () => true,
  }),
);

// Error link to handle 401 unauthorized errors
const errorLink = onError(({ graphQLErrors, networkError }: any) => {
  // Check for 401 in network errors
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    console.log('Apollo: 401 Unauthorized - triggering auth expired');
    authEvents.emitAuthExpired();
    return;
  }

  // Check for auth errors in GraphQL errors
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.message?.includes('expecting a value for non-nullable variable')) {
        console.log('Apollo: Ignoring variable validation error (subscription reconnect)');
        return;
      }

      if (
        err.extensions?.code === 'access-denied' ||
        err.extensions?.code === 'invalid-jwt' ||
        err.message?.includes('JWTExpired') ||
        err.message?.includes('Could not verify JWT')
      ) {
        console.log('Apollo: JWT/Auth error - triggering auth expired');
        authEvents.emitAuthExpired();
        return;
      }
    }
  }
});

// Split link: use WebSocket for subscriptions, HTTP for everything else
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

export const apolloClient = new ApolloClient({
  link: from([errorLink, splitLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          people: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          families: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          family_data: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          person_data: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          event: {
            merge(existing = [], incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

export default apolloClient;
